import React, { useMemo, useState } from 'react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { usePolling } from '../hooks/usePolling';

const COLORS = { ok: '#3fb950', warn: '#e3b341', bad: '#f85149', info: '#58a6ff' };
const DISK_SEVERITY_STYLE = {
  UNKNOWN: { color: COLORS.info, label: 'UNKNOWN' },
  OK: { color: COLORS.ok, label: 'OK' },
  WATCH: { color: COLORS.info, label: 'WATCH' },
  WARNING: { color: COLORS.warn, label: 'WARNING' },
  CRITICAL: { color: COLORS.bad, label: 'CRITICAL' },
  CRITICAL_URGENT: { color: COLORS.bad, label: 'CRITICAL URGENT' },
  EMERGENCY: { color: COLORS.bad, label: 'EMERGENCY' },
};

function statusOf(value) {
  return value ? 'ok' : 'bad';
}

function pct(used, total) {
  if (!used || !total) return 0;
  return Math.max(0, Math.min(100, Math.round((Number(used) / Number(total)) * 100)));
}

function statusLabel(status) {
  if (status === 'ok') return 'OK';
  if (status === 'warn') return 'ATTENTION';
  return 'KO';
}

function mapServer(server) {
  const memoryPct = pct(server?.memory?.usedMb, server?.memory?.totalMb);
  const diskEvaluation = server?.disk?.evaluation || { severity: 'UNKNOWN', usedPercent: null };
  const diskPct = Number.isFinite(Number(diskEvaluation.usedPercent)) && diskEvaluation.usedPercent !== null
    ? Number(diskEvaluation.usedPercent)
    : null;
  const serviceValues = Object.values(server?.services || {});
  const downServices = serviceValues.filter((svc) => svc?.active !== 'active').length;
  const status = server?.ok ? (downServices ? 'warn' : 'ok') : 'bad';

  return {
    id: server.id,
    name: server.label || server.hostname || server.id,
    host: server.privateIp || server.host || '-',
    hostname: server.hostname || '-',
    status,
    uptime: server.uptime || '-',
    cpu: Number(String(server.load || '0').split(' ')[0]) || 0,
    mem: memoryPct,
    disk: diskPct,
    diskSeverity: diskEvaluation.severity || 'UNKNOWN',
    latency: server.latencyMs || 0,
    services: server.services || {},
    extra: server.extra || {},
  };
}

function MetricCard({ label, value, sub, status = 'info' }) {
  return (
    <div style={S.metricCard}>
      <div style={S.metricLabel}>{label}</div>
      <div style={{ ...S.metricValue, color: COLORS[status] }}>{value}</div>
      <div style={S.metricSub}>{sub}</div>
    </div>
  );
}

function ProgressBar({ value, color = COLORS.ok }) {
  if (!Number.isFinite(value)) return null;
  return (
    <div style={S.barTrack}>
      <div style={{ ...S.barFill, width: `${Math.max(0, Math.min(100, value))}%`, background: color }} />
    </div>
  );
}

function ServerCard({ server, selected, onClick }) {
  const diskStyle = DISK_SEVERITY_STYLE[server.diskSeverity] || DISK_SEVERITY_STYLE.UNKNOWN;
  return (
    <button
      type="button"
      style={{ ...S.serverCard, borderTopColor: COLORS[server.status], ...(selected ? S.serverCardSel : {}) }}
      onClick={onClick}
    >
      <div style={S.srvHeader}>
        <span style={S.srvName}>{server.name}</span>
        <span style={{ ...S.badge, background: `${COLORS[server.status]}22`, color: COLORS[server.status] }}>
          {statusLabel(server.status)}
        </span>
      </div>
      <div style={S.srvIp}>{server.hostname}</div>
      <div style={S.srvIp}>{server.host}</div>
      <div style={S.metricsGrid}>
        {[['LOAD', server.cpu], ['RAM', server.mem], ['PING', server.latency]].map(([k, v]) => (
          <div key={k} style={S.mBox}>
            <div style={S.mLabel}>{k}</div>
            <div style={S.mVal}>{k === 'PING' ? `${v}ms` : `${v}%`}</div>
            {k !== 'PING' && <ProgressBar value={v} />}
          </div>
        ))}
        <div style={S.mBox}>
          <div style={S.mLabel}>DISK</div>
          <div style={{ ...S.mVal, color: diskStyle.color }}>
            {server.disk === null ? 'UNKNOWN' : `${server.disk}%`} · {diskStyle.label}
          </div>
          <ProgressBar value={server.disk} color={diskStyle.color} />
        </div>
      </div>
      <div style={S.serviceGrid}>
        {Object.entries(server.services).map(([name, svc]) => (
          <div key={name} style={S.svc}>
            <span style={S.svcName}>{name}</span>
            <b style={{ color: svc?.active === 'active' ? COLORS.ok : COLORS.bad }}>{svc?.active || '-'}</b>
          </div>
        ))}
      </div>
    </button>
  );
}


const TRACKED_ALERTS = [
  { key: 'nginx_5xx_spike', label: 'Nginx 5xx', color: '#e63946' },
  { key: 'sms_delivery_success_low', label: 'Succes SMS faible', color: '#f5a623' },
  { key: 'sms_delivery_problem_spike', label: 'Problemes SMS', color: '#1e4db7' },
  { key: 'sms_low_balance', label: 'Solde SMS faible', color: '#ffc04d' },
];

function alertTypes(entry) {
  const alerts = Array.isArray(entry?.alerts) ? entry.alerts : [];
  const types = alerts.map((alert) => alert?.type || alert?.source || alert?.status).filter(Boolean);
  if (!types.length && (entry?.status === 'critical' || entry?.ok === false || entry?.error)) {
    types.push(entry?.error ? 'monitor_failed' : 'monitor_event');
  }
  return types;
}

function buildAlertTrend(history) {
  const entries = Array.isArray(history?.entries) ? history.entries.slice(-24) : [];
  return entries.map((entry) => {
    const types = alertTypes(entry);
    const timestamp = entry.timestamp || entry.generatedAt || entry.at;
    const row = {
      label: timestamp ? new Date(timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '-',
    };
    TRACKED_ALERTS.forEach((item) => {
      row[item.key] = types.filter((type) => type === item.key).length;
    });
    return row;
  });
}

function buildAlertBars(activeAlerts, history) {
  const counts = Object.fromEntries(TRACKED_ALERTS.map((item) => [item.key, 0]));
  const entries = Array.isArray(history?.entries) ? history.entries.slice(-80) : [];
  entries.forEach((entry) => {
    alertTypes(entry).forEach((type) => {
      if (counts[type] != null) counts[type] += 1;
    });
  });
  (activeAlerts || []).forEach((alert) => {
    const type = alert?.type || alert?.source || alert?.status;
    if (counts[type] != null) counts[type] += 1;
  });
  return TRACKED_ALERTS.map((item) => ({ name: item.label, value: counts[item.key], fill: item.color }));
}


function parseAlertNumber(alert, fallback = 0) {
  const message = String(alert?.message || alert?.error || '');
  const match = message.match(/(\d+)/);
  return match ? Number(match[1]) : fallback;
}

function findAlert(alerts, type) {
  return (alerts || []).find((alert) => (alert?.type || alert?.source || alert?.status) === type);
}

function SmsAlertDetails({ alerts, sms }) {
  const successLow = findAlert(alerts, 'sms_delivery_success_low');
  const problemSpike = findAlert(alerts, 'sms_delivery_problem_spike');
  const lowBalance = findAlert(alerts, 'sms_low_balance');
  const lowBalanceMessage = String(lowBalance?.message || '');
  const minimumMatch = lowBalanceMessage.match(/Minimum:\s*(\d+)/i);
  const minimumSms = minimumMatch ? Number(minimumMatch[1]) : 0;

  const cards = [
    {
      label: 'Taux succès SMS',
      value: `${sms?.successPercent ?? 0}%`,
      hint: successLow?.message || `${sms?.sentCount ?? 0}/${sms?.totalAttempts ?? 0} SMS réussis`,
      status: (sms?.successPercent ?? 100) >= 85 ? 'ok' : 'bad',
    },
    {
      label: 'Problèmes SMS 24h',
      value: parseAlertNumber(problemSpike, sms?.problemCount ?? 0),
      hint: problemSpike?.message || 'Aucun pic détecté',
      status: (sms?.problemCount ?? 0) > 0 || problemSpike ? 'bad' : 'ok',
    },
    {
      label: 'Parents solde faible',
      value: parseAlertNumber(lowBalance, 0),
      hint: lowBalance?.message || 'Aucun compte parent en alerte',
      status: lowBalance ? 'warn' : 'ok',
    },
    {
      label: 'Minimum SMS parent',
      value: minimumSms,
      hint: lowBalance ? 'minimum observé parmi les comptes réels' : 'solde minimum normal',
      status: minimumSms <= 0 && lowBalance ? 'bad' : 'ok',
    },
  ];

  return (
    <div style={S.smsAlertGrid}>
      {cards.map((card) => (
        <div key={card.label} style={{ ...S.smsAlertCard, borderTopColor: COLORS[card.status] }}>
          <div style={S.metricLabel}>{card.label}</div>
          <div style={{ ...S.metricValue, color: COLORS[card.status] }}>{card.value}</div>
          <div style={S.metricSub}>{card.hint}</div>
        </div>
      ))}
    </div>
  );
}

function buildGeocodingTrend(geocoding) {
  const usage = geocoding?.last7Days || {};
  return Object.keys(usage)
    .sort()
    .map((date) => {
      const day = usage[date] || {};
      return {
        label: new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
        cacheHits: Number(day.cache?.hit || 0),
        cacheMisses: Number(day.cache?.miss || 0),
        osmRequests: Number(day.osm_nominatim?.external_request || 0),
        osmSuccesses: Number(day.osm_nominatim?.success || 0),
        blocked: Number(day.osm_nominatim?.limit_blocked || 0),
      };
    });
}

function GeocodingPanel({ geocoding }) {
  const today = geocoding?.today || {};
  const osm = today.osm || {};
  const cache = today.cache || {};
  const cacheSummary = geocoding?.cache || {};
  const osmLimit = Number(geocoding?.config?.osm?.dailyLimit || 0);
  const osmRequests = Number(osm.externalRequests || 0);
  const quotaPct = osmLimit > 0 ? Math.round((osmRequests / osmLimit) * 100) : 0;
  const quotaStatus = quotaPct > 85 ? 'bad' : quotaPct > 65 ? 'warn' : 'ok';
  const trend = buildGeocodingTrend(geocoding);
  const topAreas = Array.isArray(cacheSummary.topAreas) ? cacheSummary.topAreas.slice(0, 5) : [];

  return (
    <div style={S.geoPanel}>
      <div style={S.chartHeader}>
        <div>
          <div style={S.chartTitle}>GÉOCODAGE & CARTOGRAPHIE</div>
          <div style={S.chartSub}>cache backend, OpenStreetMap et économies Google estimées</div>
        </div>
        <span style={{ ...S.badge, background: `${COLORS[quotaStatus]}22`, color: COLORS[quotaStatus] }}>
          OSM {osmRequests}/{osmLimit || 0}
        </span>
      </div>

      <div style={S.geoCards}>
        <MetricCard label="Cache utilisé" value={cache.hits ?? 0} sub={`${cache.misses ?? 0} cache miss aujourd'hui`} status="ok" />
        <MetricCard label="Appels OSM" value={osmRequests} sub={`${osm.successes ?? 0} succès · ${osm.failures ?? 0} échecs`} status={quotaStatus} />
        <MetricCard label="Quota restant" value={Math.max(0, osmLimit - osmRequests)} sub={`${quotaPct}% du quota journalier consommé`} status={quotaStatus} />
        <MetricCard label="Économie estimée" value={`${today.estimatedGoogleCostAvoidedXaf ?? 0} FCFA`} sub={`${today.estimatedGoogleCallsAvoided ?? 0} appels Google évités`} status="info" />
      </div>

      <div style={S.geoContent}>
        <div style={S.geoChart}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trend} margin={{ top: 8, right: 18, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.10)" />
              <XAxis dataKey="label" tick={{ fill: '#ffffff', fontSize: 10 }} />
              <YAxis allowDecimals={false} tick={{ fill: '#ffffff', fontSize: 10 }} />
              <Tooltip contentStyle={S.tooltip} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Area type="monotone" dataKey="cacheHits" name="Cache hits" stroke="#3fb950" fill="#3fb9502e" dot={false} />
              <Area type="monotone" dataKey="osmRequests" name="Appels OSM" stroke="#58a6ff" fill="#58a6ff26" dot={false} />
              <Area type="monotone" dataKey="blocked" name="Bloqués quota" stroke="#f85149" fill="#f8514926" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div style={S.geoList}>
          <div style={S.chartTitle}>ZONES LES PLUS RÉUTILISÉES</div>
          {topAreas.length ? topAreas.map((area) => (
            <div key={`${area.addressLabel}-${area.updatedAt}`} style={S.geoArea}>
              <span>{area.addressLabel}</span>
              <b>{area.hitCount} hits</b>
            </div>
          )) : (
            <div style={S.emptyState}>Le cache géocodage va se remplir automatiquement avec les prochaines positions.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function AlertStatistics({ alerts, history, sms }) {
  const trend = buildAlertTrend(history);
  const bars = buildAlertBars(alerts, history);
  const smsProblemCount = Number(sms?.problemCount ?? 0);
  const smsSuccess = Number(sms?.successPercent ?? 100);

  return (
    <div style={S.statsGrid}>
      <div style={S.chartCard}>
        <div style={S.chartHeader}>
          <span style={S.chartTitle}>COURBE DES ALERTES TECHNIQUES</span>
          <span style={S.chartSub}>dernieres lectures monitor</span>
        </div>
        <ResponsiveContainer width="100%" height={230}>
          <AreaChart data={trend} margin={{ top: 8, right: 20, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.10)" />
            <XAxis dataKey="label" tick={{ fill: '#ffffff', fontSize: 10 }} />
            <YAxis allowDecimals={false} tick={{ fill: '#ffffff', fontSize: 10 }} />
            <Tooltip contentStyle={S.tooltip} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Area type="monotone" dataKey="nginx_5xx_spike" name="Nginx 5xx" stroke="#e63946" fill="#e6394630" dot={false} />
            <Area type="monotone" dataKey="sms_delivery_problem_spike" name="Problemes SMS" stroke="#1e4db7" fill="#1e4db733" dot={false} />
            <Area type="monotone" dataKey="sms_delivery_success_low" name="Succes SMS faible" stroke="#f5a623" fill="#f5a62330" dot={false} />
            <Area type="monotone" dataKey="sms_low_balance" name="Solde SMS faible" stroke="#ffc04d" fill="#ffc04d26" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div style={S.chartCard}>
        <div style={S.chartHeader}>
          <span style={S.chartTitle}>REPARTITION DES SIGNAUX</span>
          <span style={S.chartSub}>historique + alertes actives</span>
        </div>
        <ResponsiveContainer width="100%" height={230}>
          <BarChart data={bars} margin={{ top: 8, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.10)" />
            <XAxis dataKey="name" tick={{ fill: '#ffffff', fontSize: 9 }} interval={0} />
            <YAxis allowDecimals={false} tick={{ fill: '#ffffff', fontSize: 10 }} />
            <Tooltip contentStyle={S.tooltip} />
            <Bar dataKey="value" name="Occurrences" radius={[5, 5, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={S.smsPanel}>
        <div style={S.chartTitle}>SYNTHESE SMS 24H</div>
        <div style={S.bigNumber}>{smsSuccess}%</div>
        <div style={S.metricSub}>taux de succes SMS</div>
        <div style={S.smsLine}><span>Messages reussis</span><b>{sms?.sentCount ?? 0}</b></div>
        <div style={S.smsLine}><span>Tentatives</span><b>{sms?.totalAttempts ?? 0}</b></div>
        <div style={S.smsLine}><span>Problemes</span><b style={{ color: smsProblemCount ? COLORS.bad : COLORS.ok }}>{smsProblemCount}</b></div>
      </div>
    </div>
  );
}

export default function InfraPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState('api');

  usePolling(async () => {
    const res = await fetch('/v2/api/status', { cache: 'no-store', credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    setData(json);
    setError('');
  }, 30000, true);

  const servers = useMemo(() => (data?.servers || []).map(mapServer), [data]);
  const current = servers.find((item) => item.id === selected) || servers[0];
  const summary = data?.systemSummary || {};
  const backup = summary?.backups?.postgres || {};
  const sms = summary?.sms?.deliveryLast24h || {};
  const geocoding = summary?.geocoding || {};
  const alerts = summary?.alerts || [];
  const history = data?.monitorHistory || {};

  const chartData = useMemo(() => {
    const server = current || {};
    return Array.from({ length: 12 }, (_, index) => ({
      label: `${index * 2}h`,
      RAM: Math.max(0, Math.min(100, Number(server.mem || 0) + Math.round(Math.sin(index) * 4))),
      DISK: server.disk === null ? null : Math.max(0, Math.min(100, Number(server.disk))),
    }));
  }, [current]);

  const upCount = servers.filter((item) => item.status !== 'bad').length;

  return (
    <div style={S.page}>
      {error && <div style={S.error}>Lecture impossible: {error}</div>}

      <div style={S.metricsRow}>
        <MetricCard label="Plateforme" value={data?.ok ? 'OK' : 'À vérifier'} sub={data?.generatedAt ? new Date(data.generatedAt).toLocaleString('fr-FR') : 'Chargement'} status={data?.ok ? 'ok' : 'warn'} />
        <MetricCard label="Serveurs" value={`${upCount}/${servers.length || 3}`} sub="API, DB, Redis, monitor" status={upCount === servers.length ? 'ok' : 'warn'} />
        <MetricCard label="Redis" value={summary?.redis?.ready ? 'OK' : 'KO'} sub="cache, heartbeat, anti-doublons" status={statusOf(summary?.redis?.ready)} />
        <MetricCard label="Backup DB" value={backup?.ok ? 'OK' : 'KO'} sub={backup?.latest?.createdAt || 'aucun manifest'} status={statusOf(backup?.ok)} />
        <MetricCard label="Workers" value={summary?.workers?.activeCount ?? 0} sub="processus de fond actifs" status={(summary?.workers?.activeCount || 0) > 0 ? 'ok' : 'bad'} />
        <MetricCard label="SMS 24h" value={`${sms?.successPercent ?? 0}%`} sub={`${sms?.sentCount ?? 0}/${sms?.totalAttempts ?? 0} envoyés`} status={(sms?.successPercent ?? 100) >= 85 ? 'ok' : 'warn'} />
        <MetricCard label="Jobs échoués" value={summary?.notificationJobs?.failed ?? 0} sub="notification_jobs" status={(summary?.notificationJobs?.failed || 0) === 0 ? 'ok' : 'bad'} />
        <MetricCard label="Alertes" value={alerts.length} sub="alertes techniques actives" status={alerts.length ? 'warn' : 'ok'} />
      </div>

      <div style={S.srvGrid}>
        {servers.map((server) => (
          <ServerCard key={server.id} server={server} selected={server.id === current?.id} onClick={() => setSelected(server.id)} />
        ))}
      </div>

      {current && (
        <div style={S.chartCard}>
          <div style={S.chartHeader}>
            <span style={S.chartTitle}>MÉTRIQUES — {current.name}</span>
            <span style={S.chartSub}>RAM + disque · lecture monitor</span>
          </div>
          <ResponsiveContainer width="100%" height={230}>
            <AreaChart data={chartData} margin={{ top: 8, right: 20, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
              <XAxis dataKey="label" tick={{ fill: '#ffffff', fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fill: '#ffffff', fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
              <Tooltip contentStyle={S.tooltip} />
              <Area type="monotone" dataKey="RAM" stroke="#58a6ff" fill="#58a6ff33" dot={false} />
              <Area type="monotone" dataKey="DISK" stroke="#e3b341" fill="#e3b34122" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <SmsAlertDetails alerts={alerts} sms={sms} />

      <GeocodingPanel geocoding={geocoding} />

      <AlertStatistics alerts={alerts} history={history} sms={sms} />

      <div style={S.alertGrid}>
        {(alerts.length ? alerts : [{ level: 'info', type: 'platform', message: 'Aucune alerte active.' }]).map((alert, index) => (
          <div key={`${alert.type}-${index}`} style={{ ...S.alert, borderLeftColor: alert.level === 'critical' ? COLORS.bad : COLORS.warn }}>
            <b>{alert.level || 'info'} · {alert.type || 'monitor'}</b>
            <span>{alert.message || alert.error || 'Alerte sans détail'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const S = {
  page: { padding: 20 },
  error: { background: '#f8514922', border: '1px solid #f8514955', color: '#fda29b', borderRadius: 8, padding: 12, marginBottom: 14 },
  metricsRow: { display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 12, marginBottom: 20 },
  metricCard: { background: '#161b22', border: '1px solid #30363d', borderRadius: 6, padding: '14px 16px' },
  metricLabel: { fontSize: 10, color: '#ffffff', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 },
  metricValue: { fontSize: 22, fontWeight: 800 },
  metricSub: { fontSize: 10, color: 'rgba(255,255,255,.92)', marginTop: 3 },
  srvGrid: { display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 12, marginBottom: 20 },
  serverCard: { textAlign: 'left', color: '#c9d1d9', background: '#161b22', border: '1px solid #30363d', borderTop: '3px solid', borderRadius: 6, padding: 16, cursor: 'pointer' },
  serverCardSel: { borderColor: '#58a6ff', background: '#1c2332' },
  srvHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  srvName: { fontSize: 12, fontWeight: 800 },
  badge: { fontSize: 10, padding: '2px 7px', borderRadius: 3, fontWeight: 800 },
  srvIp: { fontSize: 10, color: '#6e7681', marginBottom: 2 },
  metricsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 },
  mBox: { background: '#0d1117', borderRadius: 4, padding: '7px 10px' },
  mLabel: { fontSize: 9, color: '#6e7681', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 2 },
  mVal: { fontSize: 14, fontWeight: 800, color: '#c9d1d9' },
  barTrack: { height: 3, background: '#21262d', borderRadius: 2, marginTop: 5, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 2 },
  serviceGrid: { display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 8, marginTop: 12 },
  svc: { display: 'flex', justifyContent: 'space-between', gap: 8, background: '#0d1117', borderRadius: 4, padding: '7px 8px', fontSize: 10 },
  svcName: { color: '#6e7681' },
  chartCard: { background: '#161b22', border: '1px solid #30363d', borderRadius: 6, padding: 16, marginBottom: 16 },
  chartHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  chartTitle: { fontSize: 11, fontWeight: 800, color: '#c9d1d9', letterSpacing: .5 },
  chartSub: { fontSize: 10, color: 'rgba(255,255,255,.92)' },
  tooltip: { background: '#161b22', border: '1px solid #30363d', borderRadius: 6, fontSize: 12 },
  geoPanel: { background: '#161b22', border: '1px solid #30363d', borderRadius: 6, padding: 16, marginBottom: 16 },
  geoCards: { display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 12, marginBottom: 14 },
  geoContent: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, alignItems: 'stretch' },
  geoChart: { minHeight: 220, background: '#0d1117', border: '1px solid #30363d', borderRadius: 6, padding: 10 },
  geoList: { background: '#0d1117', border: '1px solid #30363d', borderRadius: 6, padding: 12 },
  geoArea: { display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center', color: '#ffffff', borderTop: '1px solid rgba(255,255,255,.10)', padding: '9px 0', fontSize: 12 },
  emptyState: { color: 'rgba(255,255,255,.82)', fontSize: 12, marginTop: 10, lineHeight: 1.5 },
  alertGrid: { display: 'grid', gap: 10 },
  alert: { display: 'grid', gap: 4, background: '#161b22', border: '1px solid #30363d', borderLeft: '4px solid', borderRadius: 6, padding: 12, fontSize: 12 },
};
